import type { AssetStoryblok, MediaStoryblok } from "~/generated/sb/blok-types";
import { isImageOrVideo } from "~/lib/storyblok/isImageOrVideo";
import {
	getStoryblokAlt,
	getStoryblokFile,
	getStoryblokFocus,
	getStoryblokHeight,
	getStoryblokWidth,
} from "~/lib/storyblok/mediaFormatters";

const getMediaType = (media: AssetStoryblok) => {
	return isImageOrVideo(media.filename);
};

const isExternalUrl = (source: AssetStoryblok) => {
	return "is_external_url" in source && !!source.is_external_url;
};

const getExternalUrl = (source: AssetStoryblok) => {
	if (isExternalUrl(source) && source.filename) {
		let type = "unknown_external";
		if (source.filename.includes("youtube.com")) {
			type = "youtube";
		} else if (source.filename.includes("vimeo.com")) {
			type = "vimeo";
		}
		return {
			url: source.filename,
			alt: (source.alt || undefined) as string | undefined | null,
			width: 1000,
			height: 1000,
			objectPosition: undefined,
			type,
		};
	}
};

export const formatStoryblokMedia = (blok: MediaStoryblok | undefined) => {
	if (!blok) return { desktop: undefined, mobile: undefined };
	const { desktopMedia, mobileMedia, _uid } = blok;
	if (!desktopMedia?.filename && !mobileMedia?.filename)
		return { desktop: undefined, mobile: undefined };

	const desktop = desktopMedia
		? isExternalUrl(desktopMedia)
			? {
					file: getStoryblokFile(desktopMedia),
					...getExternalUrl(desktopMedia),
					...getMediaType(desktopMedia),
					_uid,
				}
			: {
					file: getStoryblokFile(desktopMedia),
					alt: getStoryblokAlt(desktopMedia),
					width: getStoryblokWidth(desktopMedia),
					height: getStoryblokHeight(desktopMedia),
					focus: getStoryblokFocus(desktopMedia),
					_uid,
					type: undefined,
					...getMediaType(desktopMedia),
				}
		: undefined;
	const mobile = mobileMedia
		? isExternalUrl(mobileMedia)
			? {
					file: getStoryblokFile(mobileMedia),
					...getExternalUrl(mobileMedia),
					...getMediaType(mobileMedia),
					_uid,
				}
			: {
					file: getStoryblokFile(mobileMedia),
					alt: getStoryblokAlt(mobileMedia),
					_uid,
					width: getStoryblokWidth(mobileMedia),
					height: getStoryblokHeight(mobileMedia),
					focus: getStoryblokFocus(mobileMedia),
					type: undefined,
					...getMediaType(mobileMedia),
				}
		: undefined;

	return { desktop, mobile };
};
